import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  CircularProgress,
  Container,
  Grid,
  FormControl,
  Paper,
  Card,
  CardContent,
  Fade,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import {
  CalendarMonth as CalendarIcon,
  WhatsApp as WhatsAppIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import dayjs from 'dayjs';
import {
  listEvents,
  createEvent,
  putEvent,
  getMonthlyDoctorAvailabilityAndExceptions,
  getDoctors as getAllDoctors,
} from '../services/api_service';
import { useAlert } from '../AlertContext';

const calendarList = [
  { name: 'UNIDAD 1', unit: 1 },
  { name: 'UNIDAD 2', unit: 2 },
  { name: 'UNIDAD 3', unit: 3 },
  { name: 'UNIDAD 4', unit: 4 },
];

const ModernCalendar = () => {
  // eslint-disable-next-line no-unused-vars
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [slots, setSlots] = useState([]);
  const [currentSlot, setCurrentSlot] = useState(null);
  const [appointments, setAppointments] = useState([]);
  const [editMode, setEditMode] = useState({ state: false, id: null });
  const [selectedDate, setSelectedDate] = useState(dayjs()); // Renamed from 'date' for clarity
  const [selectedDoctorId, setSelectedDoctorId] = useState(''); // Stores the ID of the currently selected doctor
  const [doctorsList, setDoctorsList] = useState([]); // List of all doctors

  // eslint-disable-next-line no-unused-vars
  const [rawAvailabilityData, setRawAvailabilityData] = useState({
    recurringAvailability: [],
    exceptions: [],
  });
  const [resolvedAvailabilityMap, setResolvedAvailabilityMap] = useState({}); // Stores resolved availability for each day of the current month

  const [loadingStates, setLoadingStates] = useState({
    schedules: false,
    doctors: false,
    booking: false,
  });

  const { showAlert } = useAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const session = JSON.parse(localStorage.getItem('session') || '{}');

  const [state, setState] = useState({
    doctor: [],
    doctors: {},
    calendar: calendarList[0],
  });

  useEffect(() => {
    fetchEvents();
  }, []);

  useEffect(() => {
    selectedDate &&
      resolvedAvailabilityMap[selectedDate.format('YYYY-MM-DD')] &&
      setSlots(resolvedAvailabilityMap[selectedDate.format('YYYY-MM-DD')]);
  }, [resolvedAvailabilityMap, selectedDate]);

  const fetchEvents = async () => {
    setLoadingStates((prev) => ({ ...prev, schedules: true, doctors: true }));
    try {
      const [eventsResponse, doctorsResponse] = await Promise.all([
        listEvents(),
        getAllDoctors(),
      ]);

      let latestEvents = [];
      if (eventsResponse?.result) {
        latestEvents = eventsResponse.result;
        setEvents(latestEvents);
      }
      if (doctorsResponse?.result) {
        const doctors = doctorsResponse.result;
        setDoctorsList(doctors);
        setState((prev) => ({
          ...prev,
          doctors: Object.groupBy(doctors, ({ name }) => name),
        }));

        // Determine which doctor to fetch availability for
        const currentId =
          selectedDoctorId || (doctors.length > 0 ? doctors[0].id : null);

        if (currentId) {
          const selectedDoctor = doctors.find((doc) => doc.id === currentId);
          if (selectedDoctor) {
            if (!selectedDoctorId) setSelectedDoctorId(currentId);

            const calendar =
              calendarList.find((c) => c.unit === selectedDoctor.unit) ||
              calendarList[0];
            setState((prev) => ({
              ...prev,
              doctor: [selectedDoctor],
              calendar,
            }));
            fetchDoctorAvailability(
              currentId,
              selectedDate || dayjs(),
              latestEvents,
              doctors
            );
          }
        }
      }
    } catch (error) {
      showAlert('Error al cargar datos', 'error');
    } finally {
      setLoadingStates((prev) => ({
        ...prev,
        schedules: false,
        doctors: false,
      }));
    }
  };

  const fetchDoctorAvailability = async (
    doctorId,
    date = dayjs(),
    currentEvents = null,
    currentDoctors = null
  ) => {
    setLoadingStates((prev) => ({ ...prev, schedules: true }));
    try {
      date = date || dayjs(); // Ensure we have a valid date, default to today if not provided
      const startDate = date.startOf('month').format('YYYY-MM-DD');
      const endDate = date.endOf('month').format('YYYY-MM-DD');

      const availabilityResponse =
        await getMonthlyDoctorAvailabilityAndExceptions(
          doctorId,
          startDate,
          endDate
        );

      setRawAvailabilityData({
        recurringAvailability: availabilityResponse.recurringAvailability || [],
        exceptions: availabilityResponse.exceptions || [],
      });

      // Find doctor name to filter events
      const doctorsToUse = currentDoctors || doctorsList;
      const selectedDoctor = doctorsToUse.find((doc) => doc.id == doctorId);
      const doctorName = selectedDoctor?.name;
      const eventsToUse = currentEvents || events;

      const doctorEvents = eventsToUse.filter(
        (e) => e.doctor === doctorName && e.status !== 'deleted'
      );

      const resolved = resolveAvailabilityForMonth(
        availabilityResponse.recurringAvailability || [],
        availabilityResponse.exceptions || [],
        date,
        doctorEvents
      );
      setResolvedAvailabilityMap(resolved);
    } catch (error) {
      showAlert('Error al cargar disponibilidad del doctor', 'error');
    } finally {
      setLoadingStates((prev) => ({ ...prev, schedules: false }));
    }
  };

  const resolveAvailabilityForMonth = (
    recurringAvailability,
    exceptions,
    monthDate, // dayjs object for the month
    doctorEvents = [] // Events for the selected doctor
  ) => {
    const resolved = {};
    const startOfMonth = monthDate.startOf('month');
    const endOfMonth = monthDate.endOf('month');

    for (
      let d = startOfMonth;
      d.isBefore(endOfMonth) || d.isSame(endOfMonth, 'day');
      d = d.add(1, 'day')
    ) {
      const dayKey = d.format('YYYY-MM-DD');
      const dayOfWeek = d.day(); // dayjs().day() returns 0 for Sunday, 1 for Monday, etc.

      let dailySlots = [];

      // Apply recurring availability
      const matchingRecurring = recurringAvailability.filter(
        (rec) => rec.day_of_week === (dayOfWeek === 0 ? 7 : dayOfWeek) // Backend uses 1-7 for Mon-Sun
      );

      if (matchingRecurring.length > 0) {
        // Assuming we take the first matching recurring availability for simplicity or combine them
        // For now, let's assume one recurring entry per day_of_week for a doctor
        const rec = matchingRecurring[0];
        dailySlots = generateTimeSlots(
          d.hour(rec.hstart.split(':')[0]).minute(rec.hstart.split(':')[1]),
          d.hour(rec.hend.split(':')[0]).minute(rec.hend.split(':')[1]),
          rec.time // Assuming 'time' is available in recurring (duration)
        );
      }

      // Apply exceptions
      const dayExceptions = exceptions.filter(
        (exc) => exc.exception_date.split('T')[0] === dayKey
      );

      for (const exception of dayExceptions) {
        if (exception.exception_type === 'unavailable_full_day') {
          dailySlots = []; // Doctor is completely unavailable
          break;
        } else if (exception.exception_type === 'available_override') {
          // This exception defines the availability for the day
          dailySlots =
            exception.time_slots?.map((slot) => ({
              start: d
                .hour(slot.hstart.split(':')[0])
                .minute(slot.hstart.split(':')[1]),
              end: d
                .hour(slot.hend.split(':')[0])
                .minute(slot.hend.split(':')[1]),
            })) || [];
        } else if (exception.exception_type === 'unavailable_partial_day') {
          const unavailableSlots =
            exception.time_slots?.map((slot) => ({
              start: d
                .hour(slot.hstart.split(':')[0])
                .minute(slot.hstart.split(':')[1]),
              end: d
                .hour(slot.hend.split(':')[0])
                .minute(slot.hend.split(':')[1]),
            })) || [];

          let filteredSlots = [];
          for (const availSlot of dailySlots) {
            let isOverlapping = false;
            for (const unavailSlot of unavailableSlots) {
              if (
                availSlot.start.isBefore(unavailSlot.end) &&
                unavailSlot.start.isBefore(availSlot.end)
              ) {
                isOverlapping = true;
                break;
              }
            }
            if (!isOverlapping) {
              filteredSlots.push(availSlot);
            }
          }
          dailySlots = filteredSlots;
        }
      }

      // Final Step: Filter out slots that are already booked
      const dayEvents = doctorEvents.filter(
        (e) => dayjs(e.start).format('YYYY-MM-DD') === dayKey
      );

      let availableSlots = [];
      for (const slot of dailySlots) {
        let isBooked = false;
        for (const event of dayEvents) {
          const eventStart = dayjs(event.start);
          const eventEnd = eventStart.add(event.duration || 30, 'minute');

          // Check for overlap: (slot.start < eventEnd) && (eventStart < slot.end)
          if (slot.start.isBefore(eventEnd) && eventStart.isBefore(slot.end)) {
            isBooked = true;
            break;
          }
        }
        if (!isBooked) {
          availableSlots.push(slot);
        }
      }

      resolved[dayKey] = availableSlots;
    }
    return resolved;
  };

  useEffect(() => {
    if (session.identification_number) {
      setAppointments(
        events.filter((e) =>
          e.description?.includes(`CC: ${session.identification_number}`)
        )
      );
    }
  }, [events, session.identification_number]);

  const generateTimeSlots = (startTime, endTime, durationMinutes) => {
    const slots = [];
    let currentTime = startTime;
    const end = endTime;

    while (currentTime.isBefore(end)) {
      const nextTime = currentTime.add(durationMinutes, 'minute');
      if (nextTime.isBefore(end) || nextTime.isSame(end)) {
        slots.push({
          start: currentTime,
          end: nextTime,
        });
      }
      currentTime = nextTime;
    }
    return slots;
  };

  const handleBooking = async () => {
    if (!currentSlot) return;

    setLoadingStates((prev) => ({ ...prev, booking: true }));
    try {
      const selectedDoctor = doctorsList.find(
        (doc) => doc.id == selectedDoctorId
      );

      const newEvent = {
        nombre: session?.user_name?.toUpperCase(),
        cedula: session?.identification_number,
        celular: session?.phone,
        fecha: dayjs(currentSlot.start).format('YYYY-MM-DD'),
        hora: dayjs(currentSlot.start).format('HH:mm:ss'),
        fechasuceso: dayjs(currentSlot.start).format('YYYY-MM-DD'),
        silla: state.calendar?.unit || 1, // Assuming state.calendar is still managed for unit
        doctor: selectedDoctor?.name || '',
        duracion: selectedDoctor?.time || 30, // Assuming 'time' property exists on doctor object
        asunto: session?.email || '',
      };

      if (editMode.state) {
        await putEvent(editMode.id, newEvent);
      } else {
        await createEvent(newEvent);
      }

      showAlert(
        editMode.state
          ? 'Cita reagendada exitosamente'
          : 'Cita agendada exitosamente',
        'success'
      );

      // Sync with external system if needed
      /*if (!editMode.state) {
        try {
          await createEventSync(newEvent);
        } catch (syncError) {
          console.warn('Sync failed but appointment was created:', syncError);
        }
      }*/

      setEditMode({ state: false, id: null });
      setCurrentSlot(null);
      setSelectedDate(null);
      fetchEvents();
    } catch (error) {
      showAlert('Error al procesar la cita', 'error');
    } finally {
      setLoadingStates((prev) => ({ ...prev, booking: false }));
    }
  };

  const openWhatsApp = () => {
    const phoneNumber = '573192746973';
    const message =
      'Hola, quiero agendar mi próxima cita, no he podido encontrar la disponibilidad que necesito en la aplicación';
    const url = `https://api.whatsapp.com/send?phone=${phoneNumber}&text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const formatScheduleDate = (date) => {
    return new Intl.DateTimeFormat('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      timeZone: 'UTC',
    }).format(new Date(date));
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #F8FFFE 0%, #FFFFFF 100%)',
        py: { xs: 2, md: 4 },
      }}
    >
      <Container maxWidth="lg">
        <Fade in={true} timeout={600}>
          <Box>
            {/* Header */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant={isMobile ? 'h4' : 'h3'}
                sx={{
                  fontWeight: 'bold',
                  color: '#2C5530',
                  mb: 1,
                }}
              >
                Agenda tu Cita
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Programa tu próxima visita dental de manera fácil y rápida
              </Typography>
            </Box>

            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 4 }}>
                <CircularProgress size={60} sx={{ color: '#94B754' }} />
              </Box>
            )}

            {!loading && (
              <Grid container spacing={4}>
                {/* My Appointments */}
                <Grid item xs={12} lg={5}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: '1px solid #E0E0E0',
                      overflow: 'hidden',
                    }}
                  >
                    <Box
                      sx={{
                        background:
                          'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                        color: 'white',
                        p: 3,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <CalendarIcon sx={{ mr: 2, fontSize: 28 }} />
                      <Typography variant="h6" fontWeight="bold">
                        Mis Citas
                      </Typography>
                    </Box>

                    <CardContent sx={{ p: 3 }}>
                      {loadingStates.schedules ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <CircularProgress size={40} />
                        </Box>
                      ) : appointments.length > 0 ? (
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                          }}
                        >
                          {appointments.map((appointment, index) => (
                            <Card
                              key={index}
                              variant="outlined"
                              sx={{
                                borderRadius: 2,
                                border: '1px solid #E8F5E8',
                                backgroundColor: '#F8FFF8',
                              }}
                            >
                              <CardContent sx={{ p: 2 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    mb: 1,
                                  }}
                                >
                                  <CheckIcon
                                    sx={{
                                      color: '#4CAF50',
                                      mr: 1,
                                      fontSize: 20,
                                    }}
                                  />
                                  <Typography
                                    variant="subtitle2"
                                    fontWeight="bold"
                                  >
                                    Cita Confirmada
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  {formatScheduleDate(appointment.start)}
                                </Typography>
                              </CardContent>
                            </Card>
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <ScheduleIcon
                            sx={{
                              fontSize: 60,
                              color: 'text.secondary',
                              mb: 2,
                            }}
                          />
                          <Typography
                            variant="h6"
                            color="text.secondary"
                            sx={{ mb: 1 }}
                          >
                            No tienes citas agendadas
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Agenda tu primera cita usando el formulario
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Paper>
                </Grid>

                {/* Booking Form */}
                <Grid item xs={12} lg={7}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: '1px solid #E0E0E0',
                      p: 4,
                    }}
                  >
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 'bold',
                        color: '#2C5530',
                        mb: 3,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <PersonIcon sx={{ mr: 1 }} />
                      {editMode.state ? 'Modificar Cita' : 'Nueva Cita'}
                    </Typography>

                    <Grid container spacing={3}>
                      {/* Doctor Selection */}
                      <Grid item xs={12} md={6}>
                        <FormControl fullWidth sx={{ marginTop: '8px' }}>
                          <InputLabel>Selecciona un doctor</InputLabel>
                          <Select
                            value={selectedDoctorId || ''}
                            label="Selecciona un doctor"
                            onChange={(e) => {
                              const docId = e.target.value;
                              setSelectedDoctorId(docId);
                              const selectedDoctor = doctorsList.find(
                                (doc) => doc.id == docId
                              );

                              if (selectedDoctor) {
                                // Assuming the 'unit' is associated with the doctor in some way,
                                // or defaulting to the first calendar unit if not explicitly tied.
                                const calendar =
                                  calendarList.find(
                                    (c) => c.unit === selectedDoctor.unit
                                  ) || calendarList[0]; // Default to first unit if not found
                                setState((prev) => ({
                                  ...prev,
                                  doctor: [selectedDoctor], // Keep it as an array for compatibility with existing logic
                                  calendar,
                                }));
                                fetchDoctorAvailability(docId, selectedDate); // Fetch availability for the selected doctor
                              }
                              setSelectedDate(null);
                              setCurrentSlot(null);
                            }}
                          >
                            {loadingStates.doctors ? (
                              <MenuItem disabled>
                                <CircularProgress size={20} />
                              </MenuItem>
                            ) : (
                              doctorsList.map((doctor) => (
                                <MenuItem value={doctor.id} key={doctor.id}>
                                  {`${doctor.name} (${doctor.speciality || 'Odontólogo'})`}
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Date Selection */}
                      <Grid item xs={12} md={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                          <DemoContainer components={['DatePicker']}>
                            <DatePicker
                              label="Selecciona una fecha"
                              value={selectedDate}
                              shouldDisableDate={(date) => {
                                if (!selectedDoctorId) return true; // Disable if no doctor is selected
                                const dayKey = date.format('YYYY-MM-DD');
                                // Disable if date is in the past, or no slots are available for the day
                                return (
                                  date.isBefore(dayjs(), 'day') ||
                                  !(resolvedAvailabilityMap[dayKey]?.length > 0)
                                );
                              }}
                              onChange={(newDate) => {
                                setSelectedDate(newDate);
                                setCurrentSlot(null);
                                // If the month changes, re-fetch availability for the new month
                                if (
                                  newDate &&
                                  selectedDoctorId &&
                                  !newDate.isSame(selectedDate, 'month')
                                ) {
                                  fetchDoctorAvailability(
                                    selectedDoctorId,
                                    newDate
                                  );
                                }
                              }}
                              sx={{ width: '100%' }}
                            />
                          </DemoContainer>
                        </LocalizationProvider>
                      </Grid>

                      {/* Time Selection */}
                      <Grid item xs={12}>
                        <FormControl fullWidth>
                          <InputLabel>Selecciona una hora</InputLabel>
                          <Select
                            value={currentSlot?.start?.toISOString() || ''}
                            label="Selecciona una hora"
                            disabled={slots.length === 0}
                            onChange={(e) => {
                              const selectedSlot = slots.find(
                                (slot) =>
                                  slot.start.toISOString() === e.target.value
                              );
                              setCurrentSlot(selectedSlot);
                            }}
                          >
                            {slots.length === 0 ? (
                              <MenuItem disabled>
                                {selectedDate
                                  ? 'No hay horarios disponibles'
                                  : 'Selecciona una fecha primero'}
                              </MenuItem>
                            ) : (
                              slots.map((slot, index) => (
                                <MenuItem
                                  value={slot.start.toISOString()}
                                  key={index}
                                >
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                    }}
                                  >
                                    <TimeIcon sx={{ mr: 1, fontSize: 18 }} />
                                    {slot.start
                                      .toDate()
                                      .toLocaleTimeString('es-ES', {
                                        hour: '2-digit',
                                        minute: '2-digit',
                                      })}
                                  </Box>
                                </MenuItem>
                              ))
                            )}
                          </Select>
                        </FormControl>
                      </Grid>

                      {/* Action Buttons */}
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                          <Button
                            variant="contained"
                            size="large"
                            onClick={handleBooking}
                            disabled={!currentSlot || loadingStates.booking}
                            startIcon={
                              loadingStates.booking ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                <CalendarIcon />
                              )
                            }
                            sx={{
                              backgroundColor:
                                'linear-gradient(135deg, #94B754 0%, #7DA647 100%)',
                              px: 4,
                              py: 1.5,
                              borderRadius: 2,
                              fontWeight: 'bold',
                              textTransform: 'none',
                              flex: { xs: '1 1 100%', sm: '0 0 auto' },
                            }}
                          >
                            {loadingStates.booking
                              ? 'Procesando...'
                              : editMode.state
                                ? 'Modificar Cita'
                                : 'Agendar Cita'}
                          </Button>

                          {editMode.state && (
                            <Button
                              variant="outlined"
                              size="large"
                              onClick={() => {
                                setEditMode({ state: false, id: null });
                                setCurrentSlot(null);
                                setSelectedDate(null);
                              }}
                              sx={{
                                borderColor: '#94B754',
                                color: '#94B754',
                                px: 4,
                                py: 1.5,
                                borderRadius: 2,
                                fontWeight: 'bold',
                                textTransform: 'none',
                              }}
                            >
                              Cancelar
                            </Button>
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Paper>

                  {/* Help Section */}
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: 4,
                      border: '1px solid #E0E0E0',
                      p: 3,
                      mt: 3,
                      textAlign: 'center',
                      backgroundColor: '#F8FFF8',
                    }}
                  >
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      ¿No encuentras la disponibilidad que necesitas?
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={openWhatsApp}
                      startIcon={<WhatsAppIcon />}
                      sx={{
                        backgroundColor: '#25D366',
                        '&:hover': { backgroundColor: '#1DA851' },
                        px: 3,
                        py: 1.5,
                        borderRadius: 2,
                        fontWeight: 'bold',
                        textTransform: 'none',
                      }}
                    >
                      Contactar por WhatsApp
                    </Button>
                  </Paper>
                </Grid>
              </Grid>
            )}
          </Box>
        </Fade>
      </Container>
    </Box>
  );
};

export default ModernCalendar;
